/**
 * @file api-consent-texts.ts
 * @description API-клиент редакций текстов согласия платформы (phase19.16,
 * партия Р3г, v0.19.62; SB134, SB135; db-design-r3g-texts §5;
 * uiux-r3g-consent-texts). Кабинет платформы, право
 * `PLATFORM_MANAGE_PERSONAL_DATA`.
 *
 * Редакции неизменяемы: «правка» = публикация редакции N+1 с
 * `expectedCurrent` (номер действующей, от которой начинали). Опередил
 * другой сотрудник — 409, неверные тексты — 422 `consent_text_invalid` со
 * списком ошибок полей (форма и проверка — shared `validateConsentTexts`).
 *
 * Два вида со своей нумерацией: `BANNER` — фразы баннера cookie, `NOTICE_RISK`
 * — окно о риске режима «Уведомление». Формы текстов — типы shared.
 */

import {
  CONSENT_TEXT_INVALID,
  CONSENT_TEXT_UNCHANGED,
  type ConsentTextIssue,
  type ConsentTextKind,
  type ConsentTextsByKind,
} from "@chaiberi/shared";

import { ApiError, apiFetch } from "./api-client";

/**
 * Откуда редакция — зеркало enum `SiteRevisionKind` (колонка `source`):
 * кабинет платформы, возврат, копия, код (редакция 1 из миграции).
 */
export type ConsentTextSource = "PUBLISH" | "ROLLBACK" | "RESTORE" | "IMPORT";

/** Строка истории (без текстов). */
export interface ConsentTextRevisionListItem {
  number: number;
  publishedAt: string;
  publishedByName: string;
  note: string;
  isCurrent: boolean;
  source?: ConsentTextSource;
}

/**
 * Охват магазинов (решение оркестратора 2026-10-23, uiux §8 п. 6): у
 * `GET BANNER/current` — сколько магазинов со счётчиками и сколько из них уже
 * опубликовали сайт с текущей редакцией. Нет поля — нет строки «Получили N из M».
 */
export interface ConsentTextShopsReach {
  /** Магазинов со счётчиками всего. */
  withCounters: number;
  /** Из них на сайте текущая редакция. */
  onCurrent: number;
}

/** Редакция целиком (`GET /:kind/current`, `GET /:kind/:number`). */
export interface ConsentTextRevision<K extends ConsentTextKind = ConsentTextKind> {
  kind: K;
  number: number;
  schemaVersion: number;
  texts: ConsentTextsByKind[K];
  note: string;
  publishedAt: string;
  publishedByName: string;
  source?: ConsentTextSource;
  shops?: ConsentTextShopsReach;
}

export interface PublishConsentTextsPayload<K extends ConsentTextKind> {
  /** Номер действующей редакции, от которой начинали правку. */
  expectedCurrent: number;
  texts: ConsentTextsByKind[K];
  /** «Что изменилось» — обязательна, до 300 знаков. */
  note: string;
}

/** Право раздела (каталог ролей: суперадмин, compliance-офицер); нового права нет. */
export const CONSENT_TEXTS_PERMISSION = "PLATFORM_MANAGE_PERSONAL_DATA";

/**
 * Предел заметки «что изменилось» (`SiteConsentTextRevision.note VarChar(300)`).
 * TODO(Р3г): брать из shared, когда там появится константа заметки редакции согласия.
 */
export const CONSENT_NOTE_MAX = 300;

const base = (kind: ConsentTextKind) => `/admin/platform/consent-texts/${kind}`;

export const consentTextsApi = {
  /** История редакций вида — по убыванию номера. */
  list: (kind: ConsentTextKind) => apiFetch<ConsentTextRevisionListItem[]>(base(kind)),
  /** Действующая редакция (наибольший номер). */
  current: <K extends ConsentTextKind>(kind: K) => apiFetch<ConsentTextRevision<K>>(`${base(kind)}/current`),
  /** Редакция по номеру. */
  get: <K extends ConsentTextKind>(kind: K, number: number) => apiFetch<ConsentTextRevision<K>>(`${base(kind)}/${number}`),
  /** Опубликовать редакцию N+1. */
  publish: <K extends ConsentTextKind>(kind: K, payload: PublishConsentTextsPayload<K>) =>
    apiFetch<{ number: number }>(base(kind), { method: "POST", json: payload }),
};

// ── Разбор отказов ──

/**
 * Коды «кто-то опубликовал раньше»: в проекте данных — `consent_text_conflict`,
 * в задании — `consent_text_revision_conflict`; принимаем оба и любой 409.
 */
const CONFLICT_CODES: ReadonlySet<string> = new Set(["consent_text_revision_conflict", "consent_text_conflict"]);

export function isConsentConflict(error: unknown): error is ApiError {
  return error instanceof ApiError && (error.status === 409 || CONFLICT_CODES.has(error.code));
}

function asIssue(value: unknown): ConsentTextIssue | null {
  if (typeof value !== "object" || value === null) return null;
  const field = "field" in value ? value.field : "path" in value ? value.path : undefined;
  const code = "code" in value ? value.code : "reason" in value ? value.reason : undefined;
  const message = "message" in value ? value.message : undefined;
  if (typeof field !== "string" || typeof code !== "string") return null;
  return {
    field,
    code: code as ConsentTextIssue["code"],
    message: typeof message === "string" && message !== "" ? message : "Проверьте текст — сервер его не принял.",
  };
}

/**
 * Ошибки полей из 422 `consent_text_invalid`. По shared `details` — список
 * `ConsentTextIssue`; на случай обёртки принимаем и `{ errors }` / `{ issues }`.
 */
export function consentIssuesFromError(error: unknown): ConsentTextIssue[] {
  if (!(error instanceof ApiError) || (error.status !== 422 && error.code !== CONSENT_TEXT_INVALID)) return [];
  const details = error.details;
  let list: unknown[] = [];
  if (Array.isArray(details)) list = details;
  else if (typeof details === "object" && details !== null) {
    const nested = "errors" in details ? details.errors : "issues" in details ? details.issues : [details];
    list = Array.isArray(nested) ? nested : [nested];
  }
  return list.map(asIssue).filter((i): i is ConsentTextIssue => i !== null);
}

/** Текст сбоя публикации для тоста/полосы — человеческим языком, без кодов. */
export function consentPublishErrorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "network_error") return "Не опубликовано — связь с сервером прервалась. Ваши правки на месте.";
    if (error.code === CONSENT_TEXT_UNCHANGED) return "Тексты совпадают с действующей редакцией — публиковать нечего.";
    if (error.status === 403) return "Нужно право «Управление персональными данными».";
    if (error.status === 422) return "Сервер не принял тексты — исправьте поля с ошибками.";
  }
  return "Не опубликовано — попробуйте ещё раз. Ваши правки на месте.";
}
